import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { SearchHit } from '@contracts/search';
import { useMeetings, useSearch } from '../api/queries';
import { Broken, Card, Chip, Empty, Icon, SkeletonRows, Who } from '../ui';
import { between, clock, dateLabel, duration } from '../lib/time';
import { useTab } from '../lib/url-tab';

type Kind = 'all' | 'lines' | 'meetings' | 'people';

/**
 * Board 17 — search, as a page. It matches names and sentences and says so:
 * a sentence hit shows the sentence with the match lit, a title hit shows the
 * meeting, a person is a plate. The count sits in the box, where the eye is.
 */
export function Search(): ReactElement {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [text, setText] = useState(q);
  const [kind, setKind] = useTab<Kind>('kind', ['all', 'lines', 'meetings', 'people'], 'all');
  useEffect(() => setText(q), [q]);
  const results = useSearch(q);
  const all = useMeetings();
  const n = all.data?.length ?? 0;
  const ready = q.trim().length >= 2;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim().length >= 2) setParams({ q: text.trim() });
  };
  const lines = (results.data?.meetings ?? []).filter((m) => m.snippet);
  const meetings = (results.data?.meetings ?? []).filter((m) => !m.snippet);
  const people = results.data?.people ?? [];
  const total = lines.length + meetings.length + people.length;

  return (
    <div className="page stack-lg" style={{ maxWidth: 820 }}>
      <form onSubmit={submit} className="search-page">
        <Icon name="search" size={18} className="search-glyph" />
        <input
          type="search"
          className="field"
          placeholder="Title, person, or something said…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
          aria-label="Search"
        />
        {results.data ? (
          <span className="mono count">
            {total} {total === 1 ? 'result' : 'results'}
          </span>
        ) : null}
      </form>
      {ready ? (
        <div className="chips">
          <Chip on={kind === 'all'} onClick={() => setKind('all')}>
            Everything
          </Chip>
          <Chip
            on={kind === 'lines'}
            count={results.data ? lines.length : undefined}
            onClick={() => setKind('lines')}
          >
            Sentences
          </Chip>
          <Chip
            on={kind === 'meetings'}
            count={results.data ? meetings.length : undefined}
            onClick={() => setKind('meetings')}
          >
            Meetings
          </Chip>
          <Chip
            on={kind === 'people'}
            count={results.data ? people.length : undefined}
            onClick={() => setKind('people')}
          >
            People
          </Chip>
        </div>
      ) : (
        <p className="t-secondary">
          Type at least two letters. Search matches names and sentences.
        </p>
      )}
      {!ready ? null : results.isPending ? (
        <Card flush>
          <SkeletonRows rows={4} />
        </Card>
      ) : results.isError ? (
        <Card flush>
          <Broken what="Search" retry={() => void results.refetch()} />
        </Card>
      ) : total === 0 ? (
        <Card flush>
          <Empty lead={`Nothing matches “${q}”.`}>
            {n
              ? `Not in ${n} meetings — and that is an answer, not a failure.`
              : 'Nothing recorded yet.'}
          </Empty>
        </Card>
      ) : (
        <div className="stack">
          {(kind === 'all' || kind === 'people') && people.length ? (
            <div className="cluster" style={{ padding: '4px 0' }}>
              {people.map((p) => (
                <Who
                  key={p.email}
                  email={p.email}
                  name={p.name}
                  to={`/people/${encodeURIComponent(p.email)}`}
                />
              ))}
            </div>
          ) : null}
          {kind === 'all' || kind === 'lines'
            ? lines.map((m) => <LineHit key={m.id} m={m} q={q} />)
            : null}
          {kind === 'all' || kind === 'meetings'
            ? meetings.map((m) => <MeetingHit key={m.id} m={m} q={q} />)
            : null}
        </div>
      )}
      {ready && results.data ? (
        <p className="t-secondary">
          Search matches names and sentences — titles, notes, transcript lines and people. For a
          judgement rather than a match, Ask arrives with Knowledge.
        </p>
      ) : null}
    </div>
  );
}

/** The matched word lit, the rest as it was. */
function Lit({ text, q }: { text: string; q: string }): ReactElement {
  const needle = q.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const parts: ReactNode[] = [];
  let rest = text;
  let i = 0;
  while (rest.length) {
    const at = rest.toLowerCase().indexOf(needle);
    if (at < 0) {
      parts.push(rest);
      break;
    }
    parts.push(rest.slice(0, at), <mark key={i++}>{rest.slice(at, at + needle.length)}</mark>);
    rest = rest.slice(at + needle.length);
  }
  return <>{parts}</>;
}

function LineHit({ m, q }: { m: SearchHit; q: string }): ReactElement {
  return (
    <Link to={`/m/${m.id}`} className="card hit">
      <span className="t-body">
        “<Lit text={(m.snippet ?? '').replace(/^#+\s*/, '')} q={q} />”
      </span>
      <span className="mono">
        {m.title} · {dateLabel(m.startedAt)} {clock(m.startedAt)}
      </span>
    </Link>
  );
}

function MeetingHit({ m, q }: { m: SearchHit; q: string }): ReactElement {
  return (
    <Link to={`/m/${m.id}`} className="card hit">
      <span className="t-body">
        <Lit text={m.title} q={q} />
      </span>
      <span className="mono">
        {duration(between(m.startedAt, m.endedAt))} · {m.commitmentCount}{' '}
        {m.commitmentCount === 1 ? 'commitment' : 'commitments'} · {m.decisionCount}{' '}
        {m.decisionCount === 1 ? 'decision' : 'decisions'}
      </span>
    </Link>
  );
}
